import { useId, useRef, type KeyboardEvent } from 'react';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import '@/components/ui/LeadingIconField/leadingIconField.css';

export interface EmailInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  id?: string;
  hint?: string;
}

export function EmailInput({
  value,
  onChange,
  disabled,
  fullWidth,
  placeholder = 'Email',
  id,
  hint,
}: EmailInputProps) {
  const reactId = useId().replace(/:/g, '');
  const inputId = id ?? `email-input-${reactId}`;
  const inputRef = useRef<HTMLInputElement>(null);

  const clear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && value) {
      e.preventDefault();
      clear();
    }
  };

  const hasClear = Boolean(value) && !disabled;

  return (
    <div
      className={`form__field ${fullWidth ? 'form__field_full' : ''} leading-icon-field ${fullWidth ? 'leading-icon-field_full-width' : ''}`}
    >
      <div
        className={`leading-icon-field__shell ${hasClear ? 'leading-icon-field__shell_has-clear' : ''}`}
      >
        <span className="leading-icon-field__icon" aria-hidden>
          <EmailOutlined sx={{ fontSize: 18, color: 'var(--text-muted)', opacity: 0.9 }} />
        </span>
        <input
          ref={inputRef}
          id={inputId}
          type="email"
          inputMode="email"
          autoComplete="email"
          spellCheck={false}
          enterKeyHint="done"
          className="leading-icon-field__input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          aria-describedby={hint ? `${inputId}-hint` : undefined}
        />
        {hasClear ? <ComboClearButton onClick={clear} className="leading-icon-field__clear" /> : null}
      </div>
      {hint ? (
        <span id={`${inputId}-hint`} className="leading-icon-field__hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}
